import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  Database,
  Image as ImageIcon,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Mail,
  Menu,
  ScrollText,
  ShieldCheck,
  Store,
  Users,
  X,
  Zap,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";

const NAV = [
  { path: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { path: "/admin/operations", label: "Operations", icon: Zap },
  { path: "/admin/stores", label: "Stores", icon: Store },
  { path: "/admin/competitors", label: "Competitors", icon: Users },
  { path: "/admin/ads", label: "Ads", icon: ImageIcon },
  { path: "/admin/jobs", label: "Background jobs", icon: ListChecks },
  { path: "/admin/emails", label: "Emails", icon: Mail },
  { path: "/admin/audit", label: "Audit", icon: ScrollText },
  { path: "/admin/health", label: "Health", icon: Activity },
  { path: "/admin/waitlist-email", label: "Waitlist email", icon: Mail },
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate("/login");
    }
  };

  return (
    <div className="min-h-screen bg-background font-body text-foreground">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed left-0 top-0 z-50 h-full w-64 bg-card border-r border-border/50 backdrop-blur-sm transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between p-4 lg:p-6 border-b border-border/50">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <ShieldCheck className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-bold tracking-[0.18em]">RADAR</h1>
              <p className="text-xs text-muted-foreground">Operator console</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close sidebar"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex flex-col h-[calc(100vh-80px)]">
          <nav className="flex-1 p-4 space-y-1">
            {NAV.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                    isActive
                      ? "bg-primary/10 text-primary border border-primary/20"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/10"
                  }`
                }
              >
                <item.icon className="w-4 h-4 shrink-0" />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            ))}

            <div className="pt-6 mt-6 border-t border-border/50">
              <div className="px-3 mb-3">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Outside admin
                </h3>
              </div>
              <NavLink
                to="/dashboard"
                className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/10"
              >
                <Database className="w-4 h-4 shrink-0" />
                <span>Merchant dashboard</span>
              </NavLink>
            </div>
          </nav>

          <div className="p-4 border-t border-border/50 space-y-2">
            <div className="px-3 py-1">
              <p className="text-xs text-muted-foreground">Signed in as</p>
              <p className="text-sm font-medium truncate" title={user?.email}>
                {user?.email || "—"}
              </p>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-muted-foreground hover:text-foreground"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      <div className="lg:ml-64">
        <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-sm border-b border-border/50">
          <div className="flex items-center justify-between px-4 lg:px-6 h-14">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open sidebar"
              >
                <Menu className="w-4 h-4" />
              </Button>
              <span className="text-sm text-muted-foreground hidden sm:inline">
                Admin
              </span>
              <span className="text-sm text-muted-foreground hidden sm:inline">/</span>
              <span className="text-sm font-medium">
                {NAV.find((n) => (n.end ? location.pathname === n.path : location.pathname.startsWith(n.path)))?.label || "—"}
              </span>
            </div>
          </div>
        </header>

        <main className="p-4 lg:p-6 max-w-screen-2xl mx-auto">
          <Outlet />
        </main>
      </div>

      <Toaster richColors closeButton position="top-right" />
    </div>
  );
}
